import { Anchor, Button, Group, SegmentedControl, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { DataTableColumn } from 'mantine-datatable';
import { Link } from 'react-router';
import { Table } from 'src/common/components/table/Table';
import type { MatchSuggestion } from '../../types';
import FeedReferenceWithComparison from './FeedReferenceWithComparison';
import MatchReasonPopover from './MatchReasonPopover';
import SelectionActions from './SelectionActions';
import { RelativeTime, ResolvedCell, StatusBadge } from './SuggestionMeta';

type SuggestionReviewTableProps = {
  suggestions: MatchSuggestion[];
  headerAction: React.ReactNode;
  showStatusFilter?: boolean;
  onConfirm: (id: string) => void;
  onReject: (id: string) => void;
  onUnlink: (id: string) => void;
  onReopen: (id: string) => void;
  onBulkConfirm?: (ids: string[]) => Promise<void>;
  onBulkReject?: (ids: string[]) => Promise<void>;
  onBulkUnlink?: (ids: string[]) => Promise<void>;
  onBulkReopen?: (ids: string[]) => Promise<void>;
};

const columns: DataTableColumn<MatchSuggestion>[] = [
  {
    accessor: 'unprocessedProduct.providerReferenceNumber',
    title: 'Feed reference',
    textAlign: 'right',
    render: suggestion => <FeedReferenceWithComparison suggestion={suggestion} />,
  },
  {
    accessor: 'suggestedAsset.providerReferenceNumber',
    title: 'Asset reference',
    render: ({ suggestedAsset }) => (
      <Anchor
        component={Link}
        to={`/client/${suggestedAsset.clientId}/financial-items/${suggestedAsset.product}/${suggestedAsset.id}`}
        size="sm"
      >
        {suggestedAsset.providerReferenceNumber}
      </Anchor>
    ),
  },
  {
    accessor: 'suggestedAsset.clientName',
    title: 'Client',
    render: ({ suggestedAsset }) => (
      <Anchor component={Link} to={`/client/${suggestedAsset.clientId}`} size="sm" fw={500}>
        {suggestedAsset.clientName}
      </Anchor>
    ),
  },
  {
    accessor: 'matchReasons',
    title: 'Why matched',
    render: suggestion => <MatchReasonPopover suggestion={suggestion} />,
  },
  {
    accessor: 'status',
    title: 'Status',
    render: ({ status }) => <StatusBadge status={status} />,
  },
  {
    accessor: 'createdAt',
    title: 'Suggested',
    render: ({ createdAt }) => <RelativeTime datetime={createdAt} />,
  },
  {
    accessor: 'resolvedAt',
    title: 'Resolved',
    render: ({ resolvedBy, resolvedAt }) => (
      <ResolvedCell resolvedBy={resolvedBy} resolvedAt={resolvedAt} />
    ),
  },
];

export default function SuggestionReviewTable({
  suggestions,
  headerAction,
  showStatusFilter = true,
  onConfirm,
  onReject,
  onUnlink,
  onReopen,
  onBulkConfirm,
  onBulkReject,
  onBulkUnlink,
  onBulkReopen,
}: SuggestionReviewTableProps) {
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [selectedRecords, setSelectedRecords] = useState<MatchSuggestion[]>([]);

  const filtered =
    !showStatusFilter || statusFilter === 'ALL'
      ? suggestions
      : suggestions.filter(s => s.status === statusFilter);

  const selectedIdSet = new Set(selectedRecords.map(r => r.id));
  const activeSelection = filtered.filter(s => selectedIdSet.has(s.id));
  const selectedIds = activeSelection.map(r => r.id);

  const [isBulkPending, setIsBulkPending] = useState(false);

  const handleBulkAction = async (action: ((ids: string[]) => Promise<void>) | undefined) => {
    if (!action) return;
    setIsBulkPending(true);
    try {
      await action(selectedIds);
      setSelectedRecords([]);
    } finally {
      setIsBulkPending(false);
    }
  };

  const actionColumn: DataTableColumn<MatchSuggestion> = {
    accessor: 'actions',
    title: '',
    textAlign: 'right',
    render: suggestion => {
      switch (suggestion.status) {
        case 'PENDING':
          return (
            <Group gap="xs" justify="flex-end">
              <Button
                variant="subtle"
                size="compact-xs"
                color="green"
                onClick={() => onConfirm(suggestion.id)}
              >
                Link
              </Button>
              <Button
                variant="subtle"
                size="compact-xs"
                color="red"
                onClick={() => onReject(suggestion.id)}
              >
                Reject
              </Button>
            </Group>
          );
        case 'CONFIRMED':
          return (
            <Button
              variant="subtle"
              size="compact-xs"
              color="gray"
              onClick={() => onUnlink(suggestion.id)}
            >
              Unlink
            </Button>
          );
        case 'REJECTED':
        case 'UNLINKED':
          return (
            <Button
              variant="subtle"
              size="compact-xs"
              color="gray"
              onClick={() => onReopen(suggestion.id)}
            >
              Reopen
            </Button>
          );
        default:
          return null;
      }
    },
  };

  const selectionActions =
    activeSelection.length > 0 ? (
      <SelectionActions
        selected={activeSelection}
        onConfirm={onBulkConfirm ? () => handleBulkAction(onBulkConfirm) : undefined}
        onReject={onBulkReject ? () => handleBulkAction(onBulkReject) : undefined}
        onUnlink={onBulkUnlink ? () => handleBulkAction(onBulkUnlink) : undefined}
        onReopen={onBulkReopen ? () => handleBulkAction(onBulkReopen) : undefined}
        onClear={() => setSelectedRecords([])}
        loading={isBulkPending}
      />
    ) : null;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="md">
          {showStatusFilter && (
            <SegmentedControl
              size="xs"
              value={statusFilter}
              onChange={value => {
                setStatusFilter(value);
                setSelectedRecords([]);
              }}
              data={[
                { value: 'PENDING', label: 'Pending' },
                { value: 'CONFIRMED', label: 'Confirmed' },
                { value: 'REJECTED', label: 'Rejected' },
                { value: 'UNLINKED', label: 'Unlinked' },
                { value: 'ALL', label: 'All' },
              ]}
            />
          )}
          <Text size="xs" c="dimmed">
            Click a feed reference to compare full details.
          </Text>
        </Group>
        {selectionActions ?? headerAction}
      </Group>
      {filtered.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No {statusFilter === 'ALL' ? '' : statusFilter.toLowerCase()} matches for this provider.
        </Text>
      ) : (
        <Table<MatchSuggestion>
          columns={[...columns, actionColumn]}
          data={filtered}
          isLoading={false}
          onRowClick={() => {}}
          selectedRecords={selectedRecords}
          onSelectedRecordsChange={setSelectedRecords}
        />
      )}
    </Stack>
  );
}
